'use client';

import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import type {
  SelectProps,
  CreatableSelectProps as CreatableFlowProps,
} from '@highlight-cp/core-ui';

import { CreatableSelectBase } from '../creatable-select/creatable-select-base';
import classes from './inline-select.module.css';

export type InlineSelectProps = SelectProps & CreatableFlowProps;

/**
 * ### InlineSelect
 *
 * @description
 * Compact Select whose input width tracks its content (value or placeholder), for
 * inline form controls in flowing text. Supports the same `creatable` flow as
 * `CreatableSelect`. Same restricted style API as the core-ui `Select`.
 *
 * @example
 * <InlineSelect data={options} value={value} placeholder="Select option" />
 *
 * @param props - {@link InlineSelectProps}
 */
const getInlineInputWidth = (
  value: SelectProps['value'],
  placeholder: SelectProps['placeholder'],
): string => `${Math.max((value?.length ?? placeholder?.length ?? 0) + 1, 15)}ch`;

export const InlineSelect = forwardRef<HTMLInputElement, InlineSelectProps>((props, ref) => (
  <CreatableSelectBase
    ref={ref}
    {...props}
    classNames={{
      root: classes.inlineRoot,
      input: classes.inlineInput,
      option: classes.inlineOption,
    }}
    style={
      {
        '--select-inline-input-width': getInlineInputWidth(props.value, props.placeholder),
      } as CSSProperties
    }
  />
));
InlineSelect.displayName = 'InlineSelect';
